import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material'
import {
  exerciseCategoriesAPI,
  interventionActivitiesAPI,
  activitiesAPI,
} from '../../services/api'
import IconPicker from '../presentation/IconPicker'
import PathwayIcon from '../presentation/PathwayIcon'

// Mirrors data-service models/intervention_exercise_categories.js.
const SLUG_PATTERN = /^[a-z0-9][a-z0-9_-]{0,49}$/
const TITLE_MAX = 80
const DESCRIPTION_MAX = 120

const ERROR_MESSAGES = {
  duplicate_slug: 'This intervention already has a category with that slug. Slugs are fixed once created, so pick a different one.',
  invalid_slug: 'Slug must be 1-50 lowercase letters, digits, "_" or "-", starting with a letter or digit.',
  invalid_icon: 'That icon is not on the allowlist; choose another.',
  invalid_title: `Title is required and at most ${TITLE_MAX} characters.`,
  invalid_description: `Description must be at most ${DESCRIPTION_MAX} characters.`,
  category_not_in_intervention: 'That category belongs to a different intervention (or was just deleted). Refresh and try again.',
  already_assigned: 'That activity is already assigned to this intervention; edit its row instead.',
  invalid_order: 'The order sent did not match the current list. Refresh and try again.',
  not_found: 'The activity or intervention no longer exists. Refresh and try again.',
  intervention_not_found: 'This intervention no longer exists.',
}

/** Message for a data-service rejection: known code → copy, else the server text. */
function apiMessage(err, fallback) {
  const data = err?.response?.data
  if (data?.code && ERROR_MESSAGES[data.code]) return ERROR_MESSAGES[data.code]
  return data?.error || data?.message || err?.message || fallback
}

function slugify(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50)
}

const isPatientInitiated = (activity) =>
  Array.isArray(activity?.entry_modes) && activity.entry_modes.includes('patient_initiated')

/** Why a patient could not launch this assignment, or null when launchable. */
function launchBlocker(assignment) {
  if (!assignment.is_active) return 'inactive activity'
  if (!isPatientInitiated(assignment)) return 'not patient-initiated'
  if (!assignment.enabled) return 'disabled here'
  return null
}

// ---------------------------------------------------------------------
// Category create / edit dialog
// ---------------------------------------------------------------------

function emptyCategoryForm() {
  return { slug: '', title: '', description: '', icon: null, enabled: true }
}

function CategoryDialog({ open, onClose, category, onSave, saving }) {
  const [form, setForm] = useState(emptyCategoryForm())
  const [error, setError] = useState(null)
  const [showErrors, setShowErrors] = useState(false)
  const editing = Boolean(category)

  useEffect(() => {
    if (open) {
      setForm(
        category
          ? {
              slug: category.slug,
              title: category.title || '',
              description: category.description || '',
              icon: category.icon || null,
              enabled: category.enabled !== false,
            }
          : emptyCategoryForm(),
      )
      setError(null)
      setShowErrors(false)
    }
  }, [open, category])

  const errors = {}
  if (!editing && !SLUG_PATTERN.test(form.slug)) errors.slug = ERROR_MESSAGES.invalid_slug
  if (!form.title.trim() || form.title.length > TITLE_MAX) errors.title = ERROR_MESSAGES.invalid_title
  if (form.description.length > DESCRIPTION_MAX) errors.description = ERROR_MESSAGES.invalid_description
  if (!form.icon) errors.icon = 'Choose an icon'
  const shown = showErrors ? errors : {}

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

  const handleSave = async () => {
    setShowErrors(true)
    setError(null)
    if (Object.keys(errors).length > 0) return
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      icon: form.icon,
      enabled: form.enabled,
    }
    if (!editing) payload.slug = form.slug
    try {
      await onSave(payload)
    } catch (err) {
      setError(apiMessage(err, 'Failed to save category'))
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? 'Edit Exercise Category' : 'New Exercise Category'}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <TextField
            size="small"
            label="Title"
            value={form.title}
            onChange={(e) => {
              const title = e.target.value
              setForm((prev) => ({
                ...prev,
                title,
                slug: !editing && (!prev.slug || prev.slug === slugify(prev.title))
                  ? slugify(title)
                  : prev.slug,
              }))
            }}
            error={Boolean(shown.title)}
            helperText={shown.title || `${form.title.length}/${TITLE_MAX}`}
            inputProps={{ maxLength: TITLE_MAX }}
            disabled={saving}
            autoFocus
            fullWidth
          />
          <TextField
            size="small"
            label="Slug"
            value={form.slug}
            onChange={(e) => set('slug', e.target.value)}
            error={Boolean(shown.slug)}
            helperText={
              shown.slug
              || (editing
                ? 'Fixed once created: the app and chat seed refer to it.'
                : 'Stable id used by the app and chat seed; cannot be changed later.')
            }
            inputProps={{ maxLength: 50, style: { fontFamily: 'monospace' } }}
            disabled={saving || editing}
            fullWidth
          />
          <TextField
            size="small"
            label="Description"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            error={Boolean(shown.description)}
            helperText={shown.description || `${form.description.length}/${DESCRIPTION_MAX}`}
            inputProps={{ maxLength: DESCRIPTION_MAX }}
            disabled={saving}
            fullWidth
          />
          <Box display="grid" gridTemplateColumns="120px 1fr" gap={1.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">Icon</Typography>
            <IconPicker
              value={form.icon}
              onChange={(icon) => set('icon', icon)}
              disabled={saving}
              error={shown.icon}
            />
            <Typography variant="body2" color="text.secondary">Enabled</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch
                size="small"
                checked={form.enabled}
                onChange={(e) => set('enabled', e.target.checked)}
                disabled={saving}
              />
              <Typography variant="caption" color="text.secondary">
                {form.enabled
                  ? 'Shown in the exercise picker when it has launchable activities'
                  : 'Hidden from the picker; its activities stay assigned'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : editing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ---------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------

/**
 * Exercise picker configuration for one intervention (plan §4): the
 * category list on the left, the assigned activities (with their
 * category) on the right. Activities are global; only their assignment
 * and category are per intervention.
 */
function ExercisesTab({ interventionId, canEdit, onError }) {
  const [categories, setCategories] = useState([])
  const [assignments, setAssignments] = useState([])
  const [allActivities, setAllActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [categoryError, setCategoryError] = useState(null)
  const [activityError, setActivityError] = useState(null)

  const [categoryDialog, setCategoryDialog] = useState({ open: false, category: null })
  const [deleteCategory, setDeleteCategory] = useState(null)

  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [assignActivity, setAssignActivity] = useState(null)
  const [assignCategoryId, setAssignCategoryId] = useState('')
  const [assignError, setAssignError] = useState(null)

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true)
      const [cats, assigned, activities] = await Promise.all([
        exerciseCategoriesAPI.getAll(interventionId),
        interventionActivitiesAPI.getAll(interventionId),
        activitiesAPI.getAll(true).then((res) => res.data || []).catch((err) => {
          console.error('Error fetching activities:', err)
          return []
        }),
      ])
      setCategories(Array.isArray(cats) ? cats : [])
      setAssignments(Array.isArray(assigned) ? assigned : [])
      setAllActivities(Array.isArray(activities) ? activities : [])
    } catch (err) {
      console.error('Error fetching exercises:', err)
      onError?.('Failed to load exercise categories and activities')
    } finally {
      setLoading(false)
    }
  }, [interventionId, onError])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const assignedByCategory = useMemo(() => {
    const counts = {}
    assignments.forEach((a) => {
      if (a.category_id != null) counts[a.category_id] = (counts[a.category_id] || 0) + 1
    })
    return counts
  }, [assignments])

  const assignedIds = useMemo(() => new Set(assignments.map((a) => a.activity_id)), [assignments])
  const unassignedActivities = useMemo(
    () => allActivities.filter((a) => !assignedIds.has(a.id)),
    [allActivities, assignedIds],
  )

  // ---- categories ----

  const refreshCategories = async () => {
    setCategories(await exerciseCategoriesAPI.getAll(interventionId))
  }

  // Rejections propagate to the dialog for inline display.
  const handleSaveCategory = async (payload) => {
    try {
      setSaving(true)
      if (categoryDialog.category) {
        await exerciseCategoriesAPI.update(interventionId, categoryDialog.category.id, payload)
      } else {
        await exerciseCategoriesAPI.create(interventionId, payload)
      }
      await refreshCategories()
      // Assignment rows embed their category; refresh so titles/icons match.
      setAssignments(await interventionActivitiesAPI.getAll(interventionId))
      setCategoryDialog({ open: false, category: null })
    } finally {
      setSaving(false)
    }
  }

  const handleToggleCategory = async (category) => {
    try {
      setSaving(true)
      setCategoryError(null)
      await exerciseCategoriesAPI.update(interventionId, category.id, { enabled: !category.enabled })
      await refreshCategories()
    } catch (err) {
      console.error('Error toggling category:', err)
      setCategoryError(apiMessage(err, 'Failed to update category'))
    } finally {
      setSaving(false)
    }
  }

  const handleMoveCategory = async (index, delta) => {
    const target = index + delta
    if (target < 0 || target >= categories.length) return
    const next = [...categories]
    const [row] = next.splice(index, 1)
    next.splice(target, 0, row)
    try {
      setSaving(true)
      setCategoryError(null)
      setCategories(await exerciseCategoriesAPI.reorder(interventionId, next.map((c) => c.id)))
    } catch (err) {
      console.error('Error reordering categories:', err)
      setCategoryError(apiMessage(err, 'Failed to reorder categories'))
      await refreshCategories().catch(() => {})
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteCategory = async () => {
    if (!deleteCategory) return
    try {
      setSaving(true)
      setCategoryError(null)
      await exerciseCategoriesAPI.remove(interventionId, deleteCategory.id)
      setDeleteCategory(null)
      await refreshCategories()
      // Assignments that pointed at it now have category null (FK SET NULL).
      setAssignments(await interventionActivitiesAPI.getAll(interventionId))
    } catch (err) {
      console.error('Error deleting category:', err)
      setCategoryError(apiMessage(err, 'Failed to delete category'))
      setDeleteCategory(null)
    } finally {
      setSaving(false)
    }
  }

  // ---- assignments ----

  const refreshAssignments = async () => {
    setAssignments(await interventionActivitiesAPI.getAll(interventionId))
  }

  const openAssignDialog = () => {
    setAssignActivity(null)
    setAssignCategoryId('')
    setAssignError(null)
    setAssignDialogOpen(true)
  }

  const handleAssign = async () => {
    if (!assignActivity) return
    try {
      setSaving(true)
      setAssignError(null)
      await interventionActivitiesAPI.assign(interventionId, {
        activity_id: assignActivity.id,
        category_id: assignCategoryId === '' ? null : Number(assignCategoryId),
      })
      await refreshAssignments()
      setAssignDialogOpen(false)
    } catch (err) {
      console.error('Error assigning activity:', err)
      setAssignError(apiMessage(err, 'Failed to assign activity'))
      if (err.response?.data?.code === 'already_assigned') {
        refreshAssignments().catch(() => {})
      }
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateAssignment = async (assignment, patch, failure) => {
    try {
      setSaving(true)
      setActivityError(null)
      await interventionActivitiesAPI.update(interventionId, assignment.activity_id, patch)
      await refreshAssignments()
    } catch (err) {
      console.error(`Error ${failure}:`, err)
      setActivityError(apiMessage(err, `Failed ${failure}`))
      if (err.response?.data?.code === 'category_not_in_intervention') {
        refreshCategories().catch(() => {})
      }
    } finally {
      setSaving(false)
    }
  }

  const handleMoveAssignment = async (index, delta) => {
    const target = index + delta
    if (target < 0 || target >= assignments.length) return
    const next = [...assignments]
    const [row] = next.splice(index, 1)
    next.splice(target, 0, row)
    try {
      setSaving(true)
      setActivityError(null)
      setAssignments(
        await interventionActivitiesAPI.reorder(interventionId, next.map((a) => a.activity_id)),
      )
    } catch (err) {
      console.error('Error reordering activities:', err)
      setActivityError(apiMessage(err, 'Failed to reorder activities'))
      await refreshAssignments().catch(() => {})
    } finally {
      setSaving(false)
    }
  }

  const handleUnassign = async (assignment) => {
    try {
      setSaving(true)
      setActivityError(null)
      await interventionActivitiesAPI.unassign(interventionId, assignment.activity_id)
      await refreshAssignments()
    } catch (err) {
      console.error('Error unassigning activity:', err)
      setActivityError(apiMessage(err, 'Failed to unassign activity'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  const deleteAffected = deleteCategory ? assignedByCategory[deleteCategory.id] || 0 : 0

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        What the app's exercise picker offers patients in this intervention. Categories are
        this intervention's own list; activities are global definitions assigned here with a
        category. A category appears in the picker only when enabled and holding at least one
        launchable activity.
      </Typography>

      <Grid container spacing={2}>
        {/* Categories */}
        <Grid item xs={12} md={5}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" fontWeight={500}>
              Categories ({categories.length})
            </Typography>
            {canEdit && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCategoryDialog({ open: true, category: null })}
                disabled={saving}
              >
                Add Category
              </Button>
            )}
          </Box>
          {categoryError && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setCategoryError(null)}>
              {categoryError}
            </Alert>
          )}
          {categories.length === 0 ? (
            <Alert severity="info">
              No categories yet. Without categories the picker is empty; assigned activities
              stay launchable by name.
            </Alert>
          ) : (
            <Paper variant="outlined">
              <List disablePadding>
                {categories.map((category, index) => {
                  const count = assignedByCategory[category.id] || 0
                  return (
                    <ListItem
                      key={category.id}
                      divider={index < categories.length - 1}
                      sx={{ py: 0.75, px: 1.5, opacity: category.enabled ? 1 : 0.6 }}
                    >
                      <PathwayIcon
                        name={category.icon}
                        size={18}
                        sx={{ mr: 1.5, color: 'text.secondary' }}
                      />
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={500}>
                              {category.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {category.slug}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box component="span" display="flex" gap={1.5}>
                            {category.description && (
                              <Typography variant="caption" color="text.secondary" component="span" noWrap>
                                {category.description}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              color={count ? 'text.secondary' : 'warning.main'}
                              component="span"
                              sx={{ flexShrink: 0 }}
                            >
                              {count ? `${count} activit${count === 1 ? 'y' : 'ies'}` : 'empty (hidden)'}
                            </Typography>
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                      <Box display="flex" alignItems="center" gap={0}>
                        <Tooltip title={category.enabled ? 'Enabled: shown in the picker' : 'Disabled: hidden from the picker'}>
                          <span>
                            <Switch
                              size="small"
                              checked={category.enabled}
                              onChange={() => handleToggleCategory(category)}
                              disabled={!canEdit || saving}
                            />
                          </span>
                        </Tooltip>
                        {canEdit && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveCategory(index, -1)}
                              disabled={saving || index === 0}
                              aria-label="Move up"
                            >
                              <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveCategory(index, 1)}
                              disabled={saving || index === categories.length - 1}
                              aria-label="Move down"
                            >
                              <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <Tooltip title="Edit category">
                              <IconButton
                                size="small"
                                onClick={() => setCategoryDialog({ open: true, category })}
                                disabled={saving}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete category">
                              <IconButton
                                size="small"
                                onClick={() => setDeleteCategory(category)}
                                disabled={saving}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </ListItem>
                  )
                })}
              </List>
            </Paper>
          )}
        </Grid>

        {/* Assigned activities */}
        <Grid item xs={12} md={7}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" fontWeight={500}>
              Assigned activities ({assignments.length})
            </Typography>
            {canEdit && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={openAssignDialog}
                disabled={saving || unassignedActivities.length === 0}
              >
                Add Activity
              </Button>
            )}
          </Box>
          {activityError && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setActivityError(null)}>
              {activityError}
            </Alert>
          )}
          {assignments.length === 0 ? (
            <Alert severity="info">
              No activities assigned. Patients in this intervention cannot launch any exercise
              until activities are assigned here.
            </Alert>
          ) : (
            <Paper variant="outlined">
              <List disablePadding>
                {assignments.map((assignment, index) => {
                  const blocker = launchBlocker(assignment)
                  const tile = assignment.ui?.tile_title
                  return (
                    <ListItem
                      key={assignment.activity_id}
                      divider={index < assignments.length - 1}
                      sx={{ py: 0.75, px: 1.5, opacity: assignment.enabled ? 1 : 0.6 }}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="body2" fontWeight={500}>
                              {assignment.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {assignment.activity_key}
                            </Typography>
                            {blocker && (
                              <Tooltip title="Patients cannot launch this activity while this holds">
                                <Chip label={blocker} size="small" color="warning" variant="outlined" />
                              </Tooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary" component="span">
                            {assignment.category_id == null
                              ? 'No category: launchable, but not shown in the picker'
                              : tile
                                ? `Tile: “${tile}”`
                                : assignment.description || ''}
                          </Typography>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <FormControl size="small" sx={{ minWidth: 170 }}>
                          <Select
                            value={assignment.category_id == null ? '' : String(assignment.category_id)}
                            onChange={(e) =>
                              handleUpdateAssignment(
                                assignment,
                                { category_id: e.target.value === '' ? null : Number(e.target.value) },
                                'to change category',
                              )
                            }
                            displayEmpty
                            disabled={!canEdit || saving}
                            sx={{ fontSize: '0.8125rem' }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {categories.map((category) => (
                              <MenuItem key={category.id} value={String(category.id)}>
                                {category.title}
                                {!category.enabled ? ' (disabled)' : ''}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Tooltip title={assignment.enabled ? 'Enabled in this intervention' : 'Disabled: patients cannot launch it'}>
                          <span>
                            <Switch
                              size="small"
                              checked={assignment.enabled}
                              onChange={(e) =>
                                handleUpdateAssignment(
                                  assignment,
                                  { enabled: e.target.checked },
                                  'to update activity',
                                )
                              }
                              disabled={!canEdit || saving}
                            />
                          </span>
                        </Tooltip>
                        {canEdit && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveAssignment(index, -1)}
                              disabled={saving || index === 0}
                              aria-label="Move up"
                            >
                              <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveAssignment(index, 1)}
                              disabled={saving || index === assignments.length - 1}
                              aria-label="Move down"
                            >
                              <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <Tooltip title="Unassign from this intervention">
                              <IconButton
                                size="small"
                                onClick={() => handleUnassign(assignment)}
                                disabled={saving}
                              >
                                <LinkOffIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </ListItem>
                  )
                })}
              </List>
            </Paper>
          )}
        </Grid>
      </Grid>

      <CategoryDialog
        open={categoryDialog.open}
        category={categoryDialog.category}
        onClose={() => setCategoryDialog({ open: false, category: null })}
        onSave={handleSaveCategory}
        saving={saving}
      />

      {/* Delete category confirm */}
      <Dialog open={Boolean(deleteCategory)} onClose={saving ? undefined : () => setDeleteCategory(null)}>
        <DialogTitle>Delete “{deleteCategory?.title}”?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteAffected > 0
              ? `${deleteAffected} assigned activit${deleteAffected === 1 ? 'y' : 'ies'} point${deleteAffected === 1 ? 's' : ''} at this category. `
              : 'No assigned activities use this category. '}
            Deleting it keeps every activity assigned to this intervention and launchable, but
            those activities lose their category and drop out of the exercise picker until you
            assign them another one. To hide the category without losing that link, disable it
            instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCategory(null)} disabled={saving}>Cancel</Button>
          <Button onClick={handleDeleteCategory} color="error" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign activity dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={saving ? undefined : () => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Activity</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {assignError && (
              <Alert severity="error" onClose={() => setAssignError(null)}>
                {assignError}
              </Alert>
            )}
            <Autocomplete
              options={unassignedActivities}
              getOptionLabel={(option) => option.name || option.activity_key}
              value={assignActivity}
              onChange={(_, value) => setAssignActivity(value)}
              disabled={saving}
              renderInput={(params) => (
                <TextField {...params} label="Activity" placeholder="Search the global list..." />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">{option.name}</Typography>
                      {!option.is_active && <Chip label="inactive" size="small" />}
                      {!isPatientInitiated(option) && (
                        <Chip label="not patient-initiated" size="small" variant="outlined" color="warning" />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {option.activity_key}
                    </Typography>
                  </Box>
                </li>
              )}
            />
            {assignActivity && !isPatientInitiated(assignActivity) && (
              <Alert severity="warning">
                This activity does not allow patient-initiated entry, so patients will not be
                able to launch it from the picker until its entry modes are changed on the
                Activities page.
              </Alert>
            )}
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={assignCategoryId}
                label="Category"
                onChange={(e) => setAssignCategoryId(e.target.value)}
                disabled={saving}
              >
                <MenuItem value="">
                  <em>None (launchable, not in the picker)</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={String(category.id)}>
                    {category.title}
                    {!category.enabled ? ' (disabled)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleAssign} variant="contained" disabled={!assignActivity || saving}>
            {saving ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ExercisesTab
